import React from 'react';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import MarketInsightService from '@app/model/market_insight/service/MarketInsightService';
import { MarketInsightQueryKeys } from '@app/model/market_insight/service/MarketInsightQueryKeys';
import { useQuery } from '@tanstack/react-query';
import Portlet from '@app/component/portlet/Portlet';
import _isEmpty from 'lodash/isEmpty';
import SoldOutIndex from '@app/model/market_insight/SoldOutIndex';
import Rating from '@app/component/rating/Rating';
import HotelRatingType from '@app/model/hotel/HotelRatingType';
import Utilities from '@util/Utilities';

// marketAreaId: the market of the market tab; null = the market of the hotel.
const MarketInsightSoldOutIndicesFragment: React.FunctionComponent<{ statDate: string, marketAreaId: number | null }> = ({ statDate, marketAreaId }) => {

	const { companyHotel } = useSelectedCompanyHotel();

	const { data: soldOutIndices, isLoading, isSuccess } = useQuery({
		queryFn: () => MarketInsightService.findSoldOutIndices({ companyHotelId: companyHotel.id, statDate: statDate, marketAreaId: marketAreaId }),
		queryKey: MarketInsightQueryKeys.soldOutIndices(companyHotel.id, statDate, marketAreaId),
	});

	if (isLoading) {
		return <Portlet title="Sold Out" note="(*hotel star categories)">
			<div className="animate-pulse flex items-center gap-x-2">
				<div className="h-2.5 bg-gray-200 rounded-full w-full mb-2.5" />
				<div className="h-2.5 bg-gray-200 rounded-full w-full mb-2.5" />
			</div>
		</Portlet>;
	}
	if (isSuccess && soldOutIndices !== undefined && soldOutIndices !== null && !_isEmpty(soldOutIndices)) {

		return <Portlet title="Sold Out" note="(*hotel star categories)">
			<div className="grid grid-cols-1 gap-x-8 sm:grid-cols-2 text-sm/6">
				{soldOutIndices.indices.map(idx => <DisplaySoldOutIndex key={idx.rating} soldOutIndex={idx}/>)}
			</div>
		</Portlet>;
	}

	return <Portlet title="Sold Out" note="(*hotel star categories)">
		<p className="block text-xs font-light text-gray-400">Not Available</p>
	</Portlet>;
};

const DisplaySoldOutIndex: React.FunctionComponent<{soldOutIndex: SoldOutIndex}> = ({soldOutIndex}) => {
	// A class of 3 hotels or fewer has no value (null).
	const rate = soldOutIndex.rate;
	return <div className="flex justify-between items-center gap-x-2 py-1.5">
		<dt>
			<div className="flex items-center text-gray-200">
				<Rating rating={soldOutIndex.rating} ratingType={HotelRatingType.STARS}/>
			</div>
		</dt>
		<dd className="flex items-center gap-x-6">
			<div className="flex items-center gap-x-1">
				<span className={`inline-flex rounded-full w-2 h-2 ${rate == null ? 'bg-gray-300' : findBackgroundColor(rate * 100)}`}></span>
				{rate == null ?
					<div className="font-medium text-gray-400">n/a</div> :
					<div className="font-medium text-gray-900">{Utilities.formatDouble(rate * 100, 0)}%</div>}
			</div>
		</dd>
	</div>
}


const findBackgroundColor = (rating: number) => {
	if(rating < 20) return "bg-blue-500";
	if(rating < 60 && rating >= 30) return "bg-indigo-500";
	if(rating < 90 && rating >= 60) return "bg-amber-500";
	if(rating >= 90) return "bg-red-500";
	return "bg-blue-500";
}

export default MarketInsightSoldOutIndicesFragment;